import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import "../CodeConverter.css";
import { FaDownload, FaCompress, FaBrush, FaCopy, FaCheck, FaTrash } from "react-icons/fa";
import JsonEditorPanel from "../components/JsonEditorPanel";
import CommonSeoContent from "../components/CommonSeoContent";
import useEditorActions from "../utils/useEditorActions";

const JsonToCSharp = () => {

  const [inputCode, setInputCode] = useState("");
  const [outputCode, setOutputCode] = useState("");

  // Import reusable actions
  const { handleDownload, handleClear } = useEditorActions(
    setInputCode,
    outputCode,
    setOutputCode
  );

  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const toPascalCase = (str) =>
    str.replace(/(^\w|_\w)/g, (m) => m.replace('_', '').toUpperCase());

  const generateCSharpClasses = (name, obj, classes = []) => {
    let lines = [`public class ${name} {`];

    for (const key in obj) {
      const value = obj[key];
      const propName = toPascalCase(key);

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const className = `${name}_${capitalize(key)}`;
        generateCSharpClasses(className, value, classes);
        lines.push(`  public ${className} ${propName} { get; set; }`);
      } else {
        const type = inferCSharpType(value);
        lines.push(`  public ${type} ${propName} { get; set; }`);
      }
    }

    lines.push(`}`);
    classes.unshift(lines.join('\n'));
    return classes;
  };

  const inferCSharpType = (value) => {
    if (Array.isArray(value)) return 'List<object>';
    if (typeof value === 'number') return Number.isInteger(value) ? 'int' : 'double';
    if (typeof value === 'boolean') return 'bool';
    if (typeof value === 'string') return 'string';
    return 'object';
  };

  const handleConvert = () => {
    try {
      const json = JSON.parse(inputCode);
      if (typeof json !== 'object') throw new Error('Root must be an object');
      const classes = generateCSharpClasses('MyClass', json);
      setOutputCode(classes.join('\n\n'));
    } catch (e) {
      setOutputCode("// ❌ Invalid JSON:\n" + e.message);
    }
  };

    return (
      <div className="formatter-container">
        <h1>JSON ➝ C# Converter </h1>

        {/* Toolbar */}
        <div className="toolbar">
          <button onClick={handleConvert} className="btn primary">
            <FaBrush /> Convert
          </button>
          <button onClick={() => handleDownload("c", "formatted-c")} className="btn success">
            <FaDownload /> Download
          </button>
  		<button onClick={handleClear} className="btn danger">
            <FaTrash /> Clear
          </button>

        </div>

        {/* Editors */}
        <div className="editor-panels">
          <JsonEditorPanel title="Input JSON" language="json" code={inputCode} setCode={setInputCode} />
          <div className="editor-section-empty-div">
          </div>
          <JsonEditorPanel title="Output C" language="c"  code={outputCode} />
        </div>
		
		<CommonSeoContent
  title="Online JSON to C# Class Converter"
  description="Convert JSON to C# class structures instantly with jsonconverter.org. Supports nested data, arrays, and produces clean C# code with proper types."
  keywords="JSON to C#, json to csharp, json to c# converter, c# class generator, json to model c#, jsonconverter.org"
  author="jsonconverter.org"
  heading="Why Use JSON to C# Converter?"
  whyUseText="Our JSON to C# Converter allows you to instantly generate C# POCO (Plain Old CLR Object) classes from JSON data. Whether you're developing with ASP.NET, Unity, or Blazor, this tool makes it easy to map JSON fields into fully-typed C# properties. It supports nested objects, arrays, and proper casing for .NET conventions. Great for building APIs, consuming third-party data, or creating strongly-typed models. Best of all, it’s entirely browser-based — no data is sent to servers, ensuring speed and privacy."
  howToUseSteps={[
    "Paste your JSON into the input box.",
    "Click Convert to generate the C# classes.",
    "Copy or download the generated C# code.",
    "Use it in your ASP.NET, Unity, or other projects.",
  ]}
  features={[
    "Generates C# POCO classes from JSON",
    "Handles nested objects and lists",
    "Maps JSON types to proper C# types",
    "Runs completely in-browser — secure and fast",
    "No signup or installation required",
    "Option to download generated code",
  ]}
  inputTitle="Input JSON:"
  inputExample={`{
  "username": "admin",
  "role": "editor",
  "active": true
}`}
  outputTitle="Output C#:"
  outputExample={`public class User {
  public string Username { get; set; }
  public string Role { get; set; }
  public bool Active { get; set; }
}`}
  callToAction="Start converting your JSON to C# classes instantly — no signup required."
/>
      </div>
    );
};

export default JsonToCSharp;
